import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { SelectionModel } from '@angular/cdk/collections';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  A,
  DOWN_ARROW,
  END,
  ENTER,
  ESCAPE,
  HOME,
  SPACE,
  UP_ARROW,
  hasModifierKey,
} from '@angular/cdk/keycodes';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  NgForm,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from 'src/app/components/form-field/error-state-matcher';
import { FormFieldControl } from 'src/app/components/form-field/form-field.types';
import { Observable, Subject, Subscription, defer, merge } from 'rxjs';
import { startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { OptionComponent } from './option.component';
import { transformPanel } from './select-animations';
import {
  OptionLike,
  OptionSelectionChange,
  SELECT_PARENT,
  SelectChange,
  SelectParent,
} from './select.types';

let uniqueIdCounter = 0;

export const SELECT_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>(
  'app-select-scroll-strategy'
);

export function SELECT_SCROLL_STRATEGY_FACTORY(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

export const SELECT_SCROLL_STRATEGY_PROVIDER = {
  provide: SELECT_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: SELECT_SCROLL_STRATEGY_FACTORY,
};

const SELECT_PANEL_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    panelClass: 'app-select__panel--above',
  },
  {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
    panelClass: 'app-select__panel--above',
  },
];

/**
 * In-house replacement for `mat-select` (Angular Material v16). Mirrors:
 * - Trigger + listbox panel via CDK Overlay/Portal.
 * - `transformPanel` animation (120 ms scale-Y + opacity).
 * - Keyboard navigation through `ActiveDescendantKeyManager` (typeahead,
 *   Home/End/PageUp/PageDown, Enter / Space, Alt+ArrowUp/Down to close).
 * - Single + multiple selection via CDK `SelectionModel`.
 * - `ControlValueAccessor` + `FormFieldControl` bridge so `<app-form-field>`
 *   keeps rendering label / errors / hints. Validation lives on the FormControl
 *   (e.g. `Validators.required`) — the component reads it via `hasValidator`.
 *
 * Out of scope (no current consumer): `<mat-optgroup>`,
 * `<mat-select-trigger>` projection, `disableOptionCentering`, `sortComparator`.
 */
@Component({
  selector: 'app-select',
  exportAs: 'appSelect',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  animations: [transformPanel],
  host: {
    '[class]': '_hostClasses',
    role: 'combobox',
    'aria-autocomplete': 'none',
    '[attr.aria-haspopup]': '"listbox"',
    '[attr.aria-expanded]': 'panelOpen',
    '[attr.aria-controls]': 'panelOpen ? id + "-panel" : null',
    '[attr.aria-labelledby]': '_getAriaLabelledby()',
    '[attr.aria-required]': 'required.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-invalid]': 'errorState.toString()',
    '[attr.aria-activedescendant]': '_getAriaActiveDescendant()',
    '[attr.aria-label]': 'ariaLabel || null',
    '[attr.aria-describedby]': '_userAriaDescribedBy || null',
    '[attr.tabindex]': 'disabled ? -1 : 0',
    '[attr.id]': 'id',
    '(click)': '_handleTriggerClick()',
    '(keydown)': '_handleKeydown($event)',
    '(focus)': '_onFocus()',
    '(blur)': '_onBlur()',
  },
  providers: [
    {
      provide: SELECT_PARENT,
      useExisting: forwardRef(() => SelectComponent),
    },
    {
      provide: FormFieldControl,
      useExisting: forwardRef(() => SelectComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent
  implements
    SelectParent,
    ControlValueAccessor,
    FormFieldControl<any>,
    AfterContentInit,
    OnChanges,
    OnDestroy,
    OnInit
{
  // ---------- identity ----------
  readonly controlType = 'app-select';
  readonly stateChanges = new Subject<void>();
  readonly autofilled = false;

  // ---------- configurable inputs ----------
  @Input() placeholder = '';
  @Input() panelClass: string | string[] | Set<string> | { [k: string]: any } =
    '';
  @Input() panelWidth: string | number | null = 'auto';
  /**
   * When `true`, the overlay-pane uses `minWidth: triggerWidth` instead of
   * `width: triggerWidth`, letting the panel grow past the trigger to fit
   * long option text without ellipsis. Default `false` keeps the panel
   * locked to the trigger / form-field outline (paridade com Material v16).
   * Opt in for narrow triggers carrying user-data-driven options — e.g.
   * the `.team-filter` chip on /sick_notes (team names).
   */
  @Input()
  get fitContent(): boolean {
    return this._fitContent;
  }
  set fitContent(value: BooleanInput) {
    this._fitContent = coerceBooleanProperty(value);
  }
  private _fitContent = false;
  @Input() hideSingleSelectionIndicator = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-label') ariaLabel = '';
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-labelledby') ariaLabelledby = '';
  @Input() compareWith: (a: any, b: any) => boolean = (a, b) => a === b;
  @Input() errorStateMatcher?: ErrorStateMatcher;
  @Input() typeaheadDebounceInterval?: number;

  // ---------- id ----------
  private _id = `app-select-${uniqueIdCounter++}`;
  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || `app-select-${uniqueIdCounter++}`;
    this.stateChanges.next();
  }

  // ---------- value ----------
  private _value: any = null;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (this._isDifferentValue(this._value, newValue)) {
      if (this.options) {
        this._setSelectionByValue(newValue);
      }
      this._value = newValue;
      this._cdr.markForCheck();
      this.stateChanges.next();
    }
  }

  // ---------- multiple ----------
  /**
   * Set once at template-bind time. Cannot change after `ngAfterContentInit`
   * (the `SelectionModel` and option flags are bound to the mode). Matches
   * `mat-select` v16 behaviour. Toggling via a remote feature flag at runtime
   * is not supported — wrap with `*ngIf` to swap modes.
   */
  private _multiple = false;
  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    if (this._selectionInitialized) {
      throw new Error(
        'app-select: cannot change "multiple" after initialization'
      );
    }
    this._multiple = coerceBooleanProperty(value);
  }

  // ---------- disabled ----------
  private _disabled = false;
  @Input()
  get disabled(): boolean {
    return this._disabled || this.ngControl?.disabled === true;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  // ---------- required ----------
  private _required = false;
  @Input()
  get required(): boolean {
    return (
      this._required ||
      this.ngControl?.control?.hasValidator(Validators.required) === true
    );
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  // ---------- outputs ----------
  @Output() readonly selectionChange = new EventEmitter<SelectChange>();
  @Output() readonly valueChange = new EventEmitter<any>();
  @Output() readonly openedChange = new EventEmitter<boolean>();
  @Output() readonly opened = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();

  // ---------- queries ----------
  @ContentChildren(OptionComponent, { descendants: true })
  options!: QueryList<OptionComponent>;

  // Anchor by template ref name — querying `TemplateRef` directly would match
  // the first `<ng-template>` in the view, which is the implicit one generated
  // by the `*ngIf` desugaring on the placeholder/value spans, not our panel.
  @ViewChild('panelTemplate', { static: true })
  _panelTemplate!: TemplateRef<unknown>;

  // ---------- internal state ----------
  panelOpen = false;
  focused = false;
  _animationState: 'void' | 'showing' = 'void';
  _userAriaDescribedBy = '';

  private _selectionInitialized = false;
  private _selectionModel!: SelectionModel<OptionComponent>;
  private _keyManager!: ActiveDescendantKeyManager<OptionComponent>;
  private _errorState = false;
  private _overlayRef: OverlayRef | null = null;
  private _portal: TemplatePortal | null = null;
  private _positionStrategy: FlexibleConnectedPositionStrategy | null = null;
  private _backdropSub: Subscription | null = null;
  private _keyManagerSub: Subscription | null = null;
  private _optionSelectionsSub: Subscription | null = null;
  private _optionsChangeSub: Subscription | null = null;
  private _ngControlStatusSub: Subscription | null = null;
  private _focusedElementBeforeOpen: HTMLElement | null = null;
  private readonly _destroy$ = new Subject<void>();

  // ---------- form control hooks ----------
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  /** Emits whenever any option is selected/deselected by user input. */
  readonly optionSelectionChanges: Observable<OptionSelectionChange> = defer(
    () => {
      const list = this.options;
      if (list) {
        return list.changes.pipe(
          startWith(list),
          switchMap(() =>
            merge<OptionSelectionChange[]>(
              ...list.map((option) => option.onSelectionChange)
            )
          )
        );
      }
      return new Observable<OptionSelectionChange>();
    }
  ) as Observable<OptionSelectionChange>;

  // ---------- lifecycle ----------
  constructor(
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _elementRef: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    private _ngZone: NgZone,
    @Inject(SELECT_SCROLL_STRATEGY)
    private _scrollStrategyFactory: () => ScrollStrategy,
    @Optional() private _dir: Directionality | null,
    @Optional() private _defaultErrorStateMatcher: ErrorStateMatcher | null,
    @Optional() @Self() public ngControl: NgControl | null,
    @Optional() private _parentForm: NgForm | null,
    @Optional() private _parentFormGroup: FormGroupDirective | null,
    @Inject(DOCUMENT) private _document: Document
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._selectionModel = new SelectionModel<OptionComponent>(this.multiple);
    this.stateChanges.next();
  }

  ngAfterContentInit(): void {
    this._selectionInitialized = true;

    this._initKeyManager();
    this._observeOptionSelections();
    this._observeOptionListChanges();

    // Defer to a microtask so the synchronous option `aria-selected` flips
    // happen *after* Angular's verification CD pass. Avoids NG0100
    // (`ExpressionChangedAfterItHasBeenChecked`) on the option host binding.
    if (this._value != null) {
      Promise.resolve().then(() => {
        if (this._value != null) {
          this._setSelectionByValue(this._value);
          this._cdr.markForCheck();
        }
      });
    }

    this._observeNgControlStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] || changes['required']) {
      this.stateChanges.next();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroyOverlay();
    this._optionSelectionsSub?.unsubscribe();
    this._optionsChangeSub?.unsubscribe();
    this._keyManagerSub?.unsubscribe();
    this._ngControlStatusSub?.unsubscribe();
    this.stateChanges.complete();
    this.openedChange.complete();
    this.opened.complete();
    this.closed.complete();
  }

  // ---------- public API ----------
  open(): void {
    if (
      this.disabled ||
      !this.options?.length ||
      this.panelOpen ||
      !this._panelTemplate
    ) {
      return;
    }
    this._focusedElementBeforeOpen =
      (this._document.activeElement as HTMLElement) ?? null;
    this._openOverlay();
    this.panelOpen = true;
    this._animationState = 'showing';
    this.openedChange.emit(true);
    this.opened.emit();
    this._cdr.markForCheck();
    this.stateChanges.next();

    // Activate currently selected option (or first if none) in the key manager.
    // Material v16 legacy "option centering" was deliberately not replicated;
    // panel uses CDK's first fallback position (below-start), matching v17+.
    Promise.resolve().then(() => {
      this._activateSelectedOrFirst();
    });
  }

  close(): void {
    if (!this.panelOpen) {
      return;
    }
    // Flip `panelOpen` BEFORE destroying the overlay. `_overlayRef.dispose()`
    // synchronously emits `detachments()`, which the backdrop subscription
    // routes back into `close()` — without this guard the re-entrant call
    // would still see `panelOpen === true` and emit `openedChange.emit(false)`
    // a second time.
    this.panelOpen = false;
    this._destroyOverlay();
    this._animationState = 'void';
    this.openedChange.emit(false);
    this.closed.emit();
    this._restoreFocus();
    this._onTouched();
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  toggle(): void {
    this.panelOpen ? this.close() : this.open();
  }

  focus(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  // ---------- selected / triggerValue ----------
  get selected(): OptionComponent | OptionComponent[] {
    return this.multiple
      ? (this._selectionModel?.selected ?? [])
      : (this._selectionModel?.selected[0] as OptionComponent);
  }

  get empty(): boolean {
    return !this._selectionModel || this._selectionModel.isEmpty();
  }

  get shouldLabelFloat(): boolean {
    return this.panelOpen || !this.empty;
  }

  get errorState(): boolean {
    return this._errorState;
  }

  get triggerValue(): string {
    if (this.empty) {
      return '';
    }
    if (this.multiple) {
      const selected = (this._selectionModel.selected as OptionComponent[])
        .map((o) => o.viewValue)
        .filter((v) => !!v);
      return selected.join(', ');
    }
    return (this._selectionModel.selected[0] as OptionComponent).viewValue;
  }

  // ---------- ControlValueAccessor ----------
  writeValue(value: any): void {
    if (this.options) {
      this._setSelectionByValue(value);
    }
    this._value = value;
    this._cdr.markForCheck();
    // Programmatic value writes (FormControl.setValue / .reset) must wake the
    // surrounding `<app-form-field>` so it re-evaluates `empty` /
    // `shouldLabelFloat` — otherwise the floating label can stick after a
    // reset until the next focus / open / value change.
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  // ---------- FormFieldControl ----------
  setDescribedByIds(ids: string[]): void {
    this._userAriaDescribedBy = ids.join(' ');
  }

  onContainerClick(): void {
    if (!this.disabled) {
      this.focus();
      this.open();
    }
  }

  // ---------- SelectParent ----------
  _onOptionSelectionChange(option: OptionLike, isUserInput: boolean): void {
    if (!isUserInput) {
      return;
    }
    const opt = option as OptionComponent;

    if (this.multiple) {
      this._selectionModel.toggle(opt);
      this._syncOptionSelectedFlags();
      const newValues = (
        this._selectionModel.selected as OptionComponent[]
      ).map((o) => o.value);
      this._value = newValues;
      this._propagateChanges(this._value);
    } else {
      const previouslySelected = this._selectionModel.selected[0];
      if (previouslySelected && previouslySelected !== opt) {
        previouslySelected.deselect();
        this._selectionModel.deselect(previouslySelected);
      }
      this._selectionModel.select(opt);
      opt._setSelected(true);
      this._value = opt.value;
      this._propagateChanges(this._value);
      this.close();
    }
  }

  _onOptionTouched(): void {
    this._onTouched();
  }

  // Rebuild the selection model and propagate from the options' current
  // `selected` flags. Used by `<app-select-all>`, which flips the flags via the
  // programmatic `select()`/`deselect()` path (isUserInput: false) that
  // `_onOptionSelectionChange` ignores — mirrors the Ctrl+A handler.
  syncSelectionFromOptions(): void {
    // Select-all only applies to multiple mode; no-op in single-select so this
    // public entry point can never coerce a scalar value into an array.
    if (!this.multiple) {
      return;
    }
    this._syncSelectionFromOptions();
  }

  // ---------- internal: selection ----------
  private _setSelectionByValue(value: any): void {
    if (!this.options) {
      return;
    }

    if (this._selectionModel) {
      this._selectionModel.clear();
    }
    this.options.forEach((opt) => opt._setSelected(false));

    if (this.multiple) {
      if (Array.isArray(value)) {
        for (const v of value) {
          const matching = this.options.find((opt) =>
            this.compareWith(opt.value, v)
          );
          if (matching) {
            this._selectionModel.select(matching);
            matching._setSelected(true);
          }
        }
      }
    } else if (value !== null && value !== undefined) {
      const matching = this.options.find((opt) =>
        this.compareWith(opt.value, value)
      );
      if (matching) {
        this._selectionModel.select(matching);
        matching._setSelected(true);
      }
    }
    this._cdr.markForCheck();
  }

  private _syncOptionSelectedFlags(): void {
    const selected = new Set(this._selectionModel.selected);
    this.options.forEach((opt) => opt._setSelected(selected.has(opt)));
  }

  private _propagateChanges(value: any): void {
    this._onChange(value);
    this.valueChange.emit(value);
    this.selectionChange.emit({ source: this, value });
    this._updateErrorState();
    this.stateChanges.next();
    this._cdr.markForCheck();
  }

  private _isDifferentValue(a: any, b: any): boolean {
    if (this.multiple && Array.isArray(a) && Array.isArray(b)) {
      return a.length !== b.length || a.some((v, i) => v !== b[i]);
    }
    return a !== b;
  }

  // ---------- internal: key manager ----------
  private _initKeyManager(): void {
    this._keyManager = new ActiveDescendantKeyManager<OptionComponent>(
      this.options
    )
      .withTypeAhead(this.typeaheadDebounceInterval ?? 200)
      .withVerticalOrientation()
      .withHorizontalOrientation(this._dir?.value ?? 'ltr')
      .withHomeAndEnd()
      .withPageUpDown()
      .withAllowedModifierKeys(['shiftKey']);

    this._keyManagerSub = this._keyManager.tabOut
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.panelOpen) {
          this.close();
        }
      });

    this._keyManager.change.pipe(takeUntil(this._destroy$)).subscribe(() => {
      if (this.panelOpen && this._keyManager.activeItem) {
        this._scrollActiveOptionIntoView();
        this._cdr.markForCheck();
      } else if (
        !this.panelOpen &&
        this._keyManager.activeItem &&
        !this.multiple
      ) {
        // Typeahead while closed (single-select): commit immediately.
        this._keyManager.activeItem._selectViaInteraction();
      }
    });
  }

  /**
   * Scrolls the panel so the currently-active option is fully inside the
   * scrollable viewport. Mirrors mat-select's `_scrollOptionIntoView` so
   * keyboard navigation past the visible window keeps the highlighted item
   * on screen.
   */
  private _scrollActiveOptionIntoView(): void {
    if (!this._overlayRef || !this._keyManager?.activeItem) {
      return;
    }
    const panel = this._overlayRef.overlayElement.querySelector(
      '.app-select__panel'
    ) as HTMLElement | null;
    const optionEl = this._keyManager.activeItem.getElementRef?.()
      ?.nativeElement as HTMLElement | undefined;
    if (!panel || !optionEl) {
      return;
    }

    const optionTop = optionEl.offsetTop;
    const optionBottom = optionTop + optionEl.offsetHeight;
    const scrollTop = panel.scrollTop;
    const panelHeight = panel.clientHeight;

    if (optionTop < scrollTop) {
      panel.scrollTop = optionTop;
    } else if (optionBottom > scrollTop + panelHeight) {
      panel.scrollTop = optionBottom - panelHeight;
    }
  }

  private _observeOptionSelections(): void {
    this._optionSelectionsSub = this.optionSelectionChanges.subscribe(
      (event) => {
        if (event.isUserInput) {
          this._onOptionSelectionChange(event.source, true);
        }
      }
    );
  }

  private _observeOptionListChanges(): void {
    // No `startWith` — the initial selection sync is performed by the
    // microtask-deferred call in `ngAfterContentInit`. Emitting synchronously
    // here would flip option `aria-selected` *during* `ngAfterContentInit`,
    // before Angular's dev-mode verification CD pass, triggering NG0100.
    this._optionsChangeSub = this.options.changes
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this._value != null) {
          this._setSelectionByValue(this._value);
        }
      });
  }

  private _activateSelectedOrFirst(): void {
    if (!this._keyManager) {
      return;
    }
    const firstSelected = this._selectionModel.selected[0];
    if (firstSelected) {
      this._keyManager.setActiveItem(firstSelected);
    } else {
      this._keyManager.setFirstItemActive();
    }
  }

  // ---------- internal: keyboard ----------
  _handleKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    this.panelOpen
      ? this._handleOpenKeydown(event)
      : this._handleClosedKeydown(event);
  }

  /**
   * `keyCode` is the historical pivot of `@angular/cdk/keycodes`, but synthetic
   * `KeyboardEvent`s constructed via `new KeyboardEvent(...)` leave `keyCode`
   * as `0`. Falling back to `event.key` keeps the handlers working both with
   * native keyboard input and with synthetic test events.
   */
  private _matchesKey(
    event: KeyboardEvent,
    code: number,
    ...keys: string[]
  ): boolean {
    return event.keyCode === code || keys.includes(event.key);
  }

  private _handleClosedKeydown(event: KeyboardEvent): void {
    const isArrowOpenable =
      this._matchesKey(event, DOWN_ARROW, 'ArrowDown') ||
      this._matchesKey(event, UP_ARROW, 'ArrowUp');
    const isCommitOpenable =
      (this._matchesKey(event, ENTER, 'Enter') ||
        this._matchesKey(event, SPACE, ' ', 'Spacebar')) &&
      !hasModifierKey(event);

    if (isArrowOpenable || isCommitOpenable) {
      event.preventDefault();
      this.open();
      return;
    }

    // Typeahead while closed (single-select). Letter keys mutate the active
    // item; the key manager `change` subscription commits immediately.
    if (!this.multiple && this._keyManager) {
      this._keyManager.onKeydown(event);
    }
  }

  private _handleOpenKeydown(event: KeyboardEvent): void {
    const isArrowKey =
      this._matchesKey(event, DOWN_ARROW, 'ArrowDown') ||
      this._matchesKey(event, UP_ARROW, 'ArrowUp');

    if (this._matchesKey(event, ESCAPE, 'Escape') && !hasModifierKey(event)) {
      event.preventDefault();
      this.close();
      return;
    }

    if (isArrowKey && event.altKey) {
      event.preventDefault();
      this.close();
      return;
    }

    if (
      (this._matchesKey(event, ENTER, 'Enter') ||
        this._matchesKey(event, SPACE, ' ', 'Spacebar')) &&
      this._keyManager.activeItem &&
      !hasModifierKey(event)
    ) {
      event.preventDefault();
      this._keyManager.activeItem._selectViaInteraction();
      return;
    }

    if (
      this.multiple &&
      this._matchesKey(event, A, 'a', 'A') &&
      event.ctrlKey
    ) {
      event.preventDefault();
      const hasDeselected = this.options.some(
        (o) => !o.disabled && !o.selected
      );
      this.options.forEach((o) => {
        if (!o.disabled) {
          hasDeselected ? o.select() : o.deselect();
        }
      });
      this._syncSelectionFromOptions();
      return;
    }

    const isHome = this._matchesKey(event, HOME, 'Home');
    const isEnd = this._matchesKey(event, END, 'End');
    if (isHome || isEnd) {
      event.preventDefault();
      isHome
        ? this._keyManager.setFirstItemActive()
        : this._keyManager.setLastItemActive();
      return;
    }

    const previousActive = this._keyManager.activeItem;
    this._keyManager.onKeydown(event);
    if (
      this.multiple &&
      isArrowKey &&
      event.shiftKey &&
      this._keyManager.activeItem &&
      this._keyManager.activeItem !== previousActive
    ) {
      this._keyManager.activeItem._selectViaInteraction();
    }
  }

  private _syncSelectionFromOptions(): void {
    this._selectionModel.clear();
    this.options.forEach((opt) => {
      if (opt.selected) {
        this._selectionModel.select(opt);
      }
    });
    const newValues = (this._selectionModel.selected as OptionComponent[]).map(
      (o) => o.value
    );
    this._value = newValues;
    this._propagateChanges(this._value);
  }

  // ---------- internal: trigger / focus ----------
  // Host click only opens the panel; close happens via Escape, outside-click, or
  // option selection. Matches mat-select v16 behaviour and avoids close-on-bubble
  // when wrapped in `<app-form-field>` (whose own click handler also calls
  // `onContainerClick` -> `open()` after our host listener).
  _handleTriggerClick(): void {
    if (!this.disabled) {
      this.open();
    }
  }

  _onFocus(): void {
    if (!this.disabled) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  _onBlur(): void {
    this.focused = false;
    if (!this.disabled && !this.panelOpen) {
      this._onTouched();
      this._updateErrorState();
      this._cdr.markForCheck();
      this.stateChanges.next();
    }
  }

  // ---------- internal: overlay ----------
  private _openOverlay(): void {
    this._destroyOverlay();
    const overlayConfig = this._buildOverlayConfig();
    this._overlayRef = this._overlay.create(overlayConfig);
    this._portal = new TemplatePortal(
      this._panelTemplate,
      this._viewContainerRef
    );
    this._overlayRef.attach(this._portal);

    // Keyboard input is captured by `(keydown)` on the panel template and by
    // the host's `(keydown)` listener. We deliberately do NOT subscribe to
    // `_overlayRef.keydownEvents()` here: CDK's `OverlayKeyboardDispatcher`
    // listens at `document.body`, so any keydown that bubbles to body would
    // be delivered to that subject too — producing a duplicate fire whenever
    // the keydown also reached the host listener (focus stayed on trigger).
    // The duplicate fire caused Space / Alt+ArrowDown to open and immediately
    // close the panel. Mat-select v16 follows the same pattern (panel-template
    // listener), not the overlay subject.

    this._backdropSub = merge(
      this._overlayRef.backdropClick(),
      this._overlayRef.detachments()
    )
      .pipe(take(1))
      .subscribe(() => this.close());

    // Recalculate width after attach so the panel matches trigger width.
    this._ngZone.onStable
      .pipe(take(1))
      .subscribe(() => this._overlayRef?.updatePosition());
  }

  private _buildOverlayConfig(): OverlayConfig {
    const triggerWidth =
      this.panelWidth === 'auto'
        ? this._elementRef.nativeElement.getBoundingClientRect().width
        : (this.panelWidth ?? undefined);

    // `transform-origin` is set in SCSS based on whether the panel ends up
    // above or below the trigger (`.app-select__panel--above` flips it).
    // Calling `withTransformOriginOn(...)` here would inline-style the origin
    // and override the SCSS rules.
    this._positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._elementRef)
      .withFlexibleDimensions(false)
      .withViewportMargin(8)
      .withLockedPosition()
      .withPositions(SELECT_PANEL_POSITIONS);

    const consumerPanelClasses = this._normalisePanelClasses(this.panelClass);

    // Default: `width: triggerWidth` locks the pane to the trigger / form-field
    // outline; long options ellipsis-truncate (paridade com mat-select v16).
    // Opt-in via `[fitContent]="true"`: `minWidth` instead of `width`, so the
    // pane can grow to fit content. Used by narrow triggers carrying long
    // user-data labels (e.g. `.team-filter` chip on /sick_notes).
    const sizingKey = this._fitContent ? 'minWidth' : 'width';
    return new OverlayConfig({
      positionStrategy: this._positionStrategy,
      hasBackdrop: true,
      backdropClass: 'app-select-overlay-backdrop',
      panelClass: ['app-select-overlay', ...consumerPanelClasses],
      direction: this._dir ?? undefined,
      scrollStrategy: this._scrollStrategyFactory(),
      [sizingKey]: triggerWidth as number | string | undefined,
    });
  }

  private _normalisePanelClasses(
    cls: string | string[] | Set<string> | { [k: string]: any }
  ): string[] {
    if (!cls) {
      return [];
    }
    if (typeof cls === 'string') {
      return cls.split(/\s+/).filter(Boolean);
    }
    if (Array.isArray(cls)) {
      return cls.filter(Boolean);
    }
    if (cls instanceof Set) {
      return Array.from(cls);
    }
    return Object.keys(cls).filter((k) => !!cls[k]);
  }

  private _destroyOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
    this._backdropSub?.unsubscribe();
    this._backdropSub = null;
    this._portal = null;
    this._positionStrategy = null;
  }

  private _restoreFocus(): void {
    // When the panel is opened via `onContainerClick` (form-field gutter
    // click without a prior focus) `document.activeElement` reports
    // `<body>`, not `null`. Treat that as "no meaningful prior focus" and
    // fall back to the host so focus is not lost in the body.
    const saved = this._focusedElementBeforeOpen;
    const isBody = saved === this._document.body;
    const target = saved && !isBody ? saved : this._elementRef.nativeElement;
    target?.focus();
    this._focusedElementBeforeOpen = null;
  }

  // ---------- internal: error state ----------
  private _observeNgControlStatus(): void {
    if (!this.ngControl?.statusChanges) {
      return;
    }
    this._ngControlStatusSub = this.ngControl.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._updateErrorState());
  }

  private _updateErrorState(): void {
    const matcher = this.errorStateMatcher ?? this._defaultErrorStateMatcher;
    const oldState = this._errorState;
    const parent = this._parentFormGroup ?? this._parentForm;
    const newState = matcher
      ? matcher.isErrorState(this.ngControl?.control ?? null, parent)
      : !!(this.ngControl?.invalid && this.ngControl?.touched);
    if (newState !== oldState) {
      this._errorState = newState;
      this.stateChanges.next();
      this._cdr.markForCheck();
    }
  }

  // ---------- internal: aria helpers ----------
  _getAriaLabelledby(): string | null {
    if (this.ariaLabel) {
      return null;
    }
    return this.ariaLabelledby || null;
  }

  _getAriaActiveDescendant(): string | null {
    if (this.panelOpen && this._keyManager?.activeItem) {
      return this._keyManager.activeItem.id;
    }
    return null;
  }

  // ---------- internal: host classes ----------
  get _hostClasses(): string {
    const classes = ['app-select'];
    if (this.disabled) classes.push('app-select--disabled');
    if (this.panelOpen) classes.push('app-select--open');
    if (this.errorState) classes.push('app-select--invalid');
    return classes.join(' ');
  }
}
